import { useContext, useMemo, useState } from 'react';
import moment from 'moment';
import { ReportsContext } from '../../../context/report/ReportContext';
import { Total } from '../../../models/interfaces';
import ReportDateRange from '../../../components/ui/ReportDateRange';
import Message from '../../../components/ui/Message';
import { downloadCsv } from '../../../utils/csv';

const numberFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

export const InvoiceResumenByTariffReport = () => {

    const { getInvoiceTariff } = useContext(ReportsContext);

    const [dateIni, setDateIni] = useState(moment().format('YYYYMMDD'));
    const [dateEnd, setDateEnd] = useState(moment().add(1, 'day').format('YYYYMMDD'));
    const [buscar, setBuscar] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<Total[]>([]);
    const [error, setError] = useState('');

    const handleCallback = (start, end) => {
        setDateIni(start.format('YYYYMMDD'));
        setDateEnd(moment(end).add(1, 'day').format('YYYYMMDD'));
    };

    const handleFilter = async () => {
        setIsLoading(true);
        try {
            const result = await getInvoiceTariff(dateIni, dateEnd);
            setData(result ?? []);
            setHasSearched(true);
        } catch (e) {
            setError('No se pudo generar el reporte, intente de nuevo.');
        } finally {
            setIsLoading(false);
        }
    };

    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        if (!term) return data;
        return data.filter(item => String(item.Descripcion ?? '').toLowerCase().includes(term));
    }, [data, buscar]);

    const totals = useMemo(() => filteredData.reduce((acc, row) => ({
        montoTotal: acc.montoTotal + Number(row.TotalMontoTotal ?? 0),
        descuento: acc.descuento + Number(row.TotalMontoDescuento ?? 0),
        subtotal: acc.subtotal + Number(row.TotalSubTotal ?? 0),
        impuesto: acc.impuesto + Number(row.TotalImpuesto ?? 0),
    }), { montoTotal: 0, descuento: 0, subtotal: 0, impuesto: 0 }), [filteredData]);

    const handleDownload = () => {
        downloadCsv('ReporteResumenPorTarifa.csv', data.map((inv) => ({
            "Descripción": inv.Descripcion,
            "Monto total": Number(inv.TotalMontoTotal).toFixed(2),
            "Monto descuento": Number(inv.TotalMontoDescuento).toFixed(2),
            "Subtotal": Number(inv.TotalSubTotal).toFixed(2),
            "Impuesto": Number(inv.TotalImpuesto).toFixed(2)
        })));
    }

    const renderBody = () => {
        if (isLoading) {
            return (
                <tr><td colSpan={5} className="text-center py-4">
                    <span className="spinner-border spinner-border-sm me-2" /> Generando reporte...
                </td></tr>
            );
        }
        if (!hasSearched) {
            return (
                <tr><td colSpan={5} className="text-center py-4 text-muted">
                    <i className="bi bi-funnel me-2" />Seleccione un rango de fechas y presione "Generar reporte".
                </td></tr>
            );
        }
        if (filteredData.length === 0) {
            return (
                <tr><td colSpan={5} className="text-center py-4 text-muted">
                    {buscar ? 'No se encontraron resultados para la búsqueda.' : 'No hay datos para el rango seleccionado.'}
                </td></tr>
            );
        }
        return filteredData.map((row, idx) => (
            <tr key={idx}>
                <td className="fw-medium">{row.Descripcion}</td>
                <td className="text-end">{numberFormatter.format(row.TotalMontoTotal)}</td>
                <td className="text-end">{numberFormatter.format(row.TotalMontoDescuento)}</td>
                <td className="text-end">{numberFormatter.format(row.TotalSubTotal)}</td>
                <td className="text-end">{numberFormatter.format(row.TotalImpuesto)}</td>
            </tr>
        ));
    };

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Resumen de facturas por tarifa</h3>
                    <p className="text-muted mb-0">Montos facturados agrupados por tarifa de impuesto.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleDownload} disabled={data.length === 0}>
                        <i className="bi bi-download me-1" /> Descargar CSV
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <div className="row g-2 align-items-end">
                        <div className="col-md-4">
                            <label className="form-label small text-muted mb-1">Rango de fechas</label>
                            <ReportDateRange onChange={handleCallback} />
                        </div>
                        <div className="col-md-3 col-lg-2 d-grid">
                            <button className="btn btn-primary" onClick={handleFilter} disabled={isLoading}>
                                <i className="bi bi-bar-chart me-1" /> Generar reporte
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Resultados */}
            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    {hasSearched && data.length > 0 && (
                        <div className="row g-2 mb-3">
                            <div className="col-md-6">
                                <div className="input-group">
                                    <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                    <input
                                        className="form-control"
                                        placeholder="Buscar por descripción..."
                                        value={buscar}
                                        onChange={e => setBuscar(e.target.value)}
                                    />
                                    {buscar && (
                                        <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                            <i className="bi bi-x-lg" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Descripción</th>
                                    <th className="text-end">Monto total</th>
                                    <th className="text-end">Monto descuento</th>
                                    <th className="text-end">Subtotal</th>
                                    <th className="text-end">Impuesto</th>
                                </tr>
                            </thead>
                            <tbody>{renderBody()}</tbody>
                            {!isLoading && filteredData.length > 0 && (
                                <tfoot>
                                    <tr className="table-light fw-semibold">
                                        <td>Total</td>
                                        <td className="text-end">{numberFormatter.format(totals.montoTotal)}</td>
                                        <td className="text-end">{numberFormatter.format(totals.descuento)}</td>
                                        <td className="text-end">{numberFormatter.format(totals.subtotal)}</td>
                                        <td className="text-end">{numberFormatter.format(totals.impuesto)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {error.length > 0 && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}
        </div>
    );
};


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
};
